import type { KeyboardEvent } from 'react';
import { Editor, Element, Transforms } from 'slate';
import isListBlock from '@atlas/tool/box/slate/isListBlock';
import matchBlockNodes from '@atlas/tool/box/slate/matchBlockNodes';
import isTextBlock from '@atlas/tool/box/slate/isTextBlock';
import getNode from '@atlas/tool/box/slate/getNode';
import removeTopLevelBlocks from '@atlas/tool/box/slate/removeTopLevelBlocks';
import isVoidBlock from '@atlas/tool/box/slate/isVoidBlock';
import isEmptyContent from '@atlas/tool/box/slate/isEmptyContent';
import isMultiLineTextBlock from '@atlas/tool/box/slate/isMultiLineTextBlock';
import { extractFirstLine } from '@atlas/tool/box/slate/extractFirstLine';
import isTableBlock from '@atlas/tool/box/slate/isTableBlock';
import areSamePoints from '@atlas/tool/box/slate/areSamePoints';
import isCalloutBlock from '@atlas/tool/box/slate/isCalloutBlock';
import type {
    SlateBlock,
    HeadingOneBlock,
    HeadingTwoBlock,
    HorizontalLineBlock,
    ImageBlock,
    ParagraphBlock,
    VideoEmbedBlock,
    VideoBlock,
    EmbedBlock,
} from '@atlas/renderer';
import changeEditorSelection from '@atlas/tool/box/slate/changeEditorSelection';
import hotkey from '@atlas/tool/box/event/hotkey';

export default function handleBackspaceAtBlockStartKeyDownEvent(editor: Editor, event: KeyboardEvent): boolean {
    if (!editor.selection) return false;
    if (event.key !== 'Backspace') return false;

    const { anchor, focus } = editor.selection;
    // Ignore if it is not beginning of the leaf
    if (anchor.offset || focus.offset) return false;

    // Ignore if it is not the first leaf in the block
    if (focus.path[focus.path.length - 1] !== 0) return false;

    // Ignore if it has selection
    if (!areSamePoints(anchor, focus)) return false;

    const [match] = matchBlockNodes(editor, { mode: 'lowest' });
    if (!match) return false;
    const [block, blockPath] = match;
    const isTopLevel = blockPath.length === 1;

    if (isTopLevel && isVoidBlock(block)) {
        event.preventDefault();
        const blockIndex = blockPath[0];
        removeTopLevelBlocks(editor, blockIndex, blockIndex, true);
        if (blockIndex > 0) {
            const endOfPrevBlock = Editor.end(editor, [blockIndex - 1]);
            changeEditorSelection(editor, {
                anchor: endOfPrevBlock,
                focus: endOfPrevBlock,
            });
        }
        return true;
    }

    if (block.type === 'table-cell') {
        event.preventDefault();
        return true;
    }

    const isParagraph = block.type === 'paragraph';

    if (isParagraph && blockPath.length === 2) {
        const topLevelBlockPath = [blockPath[0]];
        const topLevelBlock = getNode<SlateBlock>(editor, topLevelBlockPath);
        if (topLevelBlock && isCalloutBlock(topLevelBlock)) {
            event.preventDefault();
            Transforms.insertNodes(editor, block, { at: topLevelBlockPath });
            const start = Editor.start(editor, topLevelBlockPath);
            Transforms.setSelection(editor, { anchor: start, focus: start });
            Transforms.removeNodes(editor, { at: [blockPath[0] + 1] });
            return true;
        }
    }

    if (isTopLevel && isTextBlock(block)) {
        event.preventDefault();

        const blockIndex = blockPath[0];

        if (!isParagraph && isOneLineBlock(block)) {
            Transforms.setNodes(editor, { type: 'paragraph' }, { at: blockPath });
            return true;
        }

        if (!isParagraph && isMultiLineTextBlock(block)) {
            const leafs = extractFirstLine(editor, blockIndex);
            if (!leafs.length) return true;

            Transforms.insertNodes(editor, { type: 'paragraph', children: leafs }, { at: blockPath });

            const start = Editor.start(editor, blockPath);
            Transforms.setSelection(editor, { anchor: start, focus: start });

            return true;
        }

        // Merging Paragraph with previous block

        if (blockIndex === 0) {
            if (editor.children.length < 2) return true;
            if (!isEmptyContent(block.children)) return true;
            if (doubleBackspaceHotkey.matches(event)) {
                Transforms.removeNodes(editor, { at: blockPath });
            }
            return true;
        }

        const prevBlockPath = [blockIndex - 1];
        const prevBlock = getNode<SlateBlock>(editor, prevBlockPath);
        if (!prevBlock) return true;

        if (isEmptyContent(block.children)) {
            Transforms.mergeNodes(editor, { at: blockPath });
            return true;
        }

        if (isListBlock(prevBlock) || isTableBlock(prevBlock) || isTextBlock(prevBlock)) {
            const firstLine = extractFirstLine(editor, blockIndex);
            const endOfPrevBlock = Editor.end(editor, prevBlockPath);
            if (firstLine.length) {
                Transforms.insertNodes(editor, firstLine, { at: endOfPrevBlock });
            }

            changeEditorSelection(editor, { anchor: endOfPrevBlock, focus: endOfPrevBlock });
            return true;
        }

        if (isVoidBlock(prevBlock)) {
            const startOfPrevBlock = Editor.start(editor, prevBlockPath);
            Transforms.setSelection(editor, { anchor: startOfPrevBlock, focus: startOfPrevBlock });
            return true;
        }

        return false;
    }

    if (block.type === 'list-item') {
        event.preventDefault();

        const blockIndex = blockPath[blockPath.length - 1];

        if (blockIndex > 0) {
            // When list item is not the first one

            const prevBlockPath = blockPath.slice(0, -1).concat(blockIndex - 1);

            const focus = Editor.end(editor, prevBlockPath);
            changeEditorSelection(editor, { anchor: focus, focus });

            Transforms.removeNodes(editor, { at: blockPath });
            Transforms.insertNodes(editor, block.children, {
                at: Editor.end(editor, prevBlockPath),
            });

            return true;
        }

        Transforms.unwrapNodes(editor, {
            match: (node) => Element.isElement(node) && isListBlock(node),
            mode: 'lowest',
            split: true,
        });

        if (blockPath.length === 2) {
            // Top level list

            Transforms.setNodes(editor, { type: 'paragraph' }, { at: [blockPath[0]] });
        }

        return true;
    }

    return false;
}

const doubleBackspaceHotkey = hotkey(['backspace', 'backspace'], 'hidden');

const NO_MULTI_LINE_SUPPORT_BLOCK_TYPES: Array<NoMultilineSupportBlock['type']> = [
    'paragraph',
    'image',
    'video',
    'video-embed',
    'embed',
    'heading-one',
    'heading-two',
    'horizontal-line',
];

function isOneLineBlock(element: SlateBlock): element is NoMultilineSupportBlock {
    return (NO_MULTI_LINE_SUPPORT_BLOCK_TYPES as Array<SlateBlock['type']>).includes(element.type);
}

type NoMultilineSupportBlock =
    | ParagraphBlock
    | ImageBlock
    | VideoBlock
    | VideoEmbedBlock
    | EmbedBlock
    | HeadingOneBlock
    | HeadingTwoBlock
    | HorizontalLineBlock;

// #region Tests

if (import.meta.vitest) {
    const { describe, it } = import.meta.vitest;
    describe('handleBackspaceAtBlockStartKeyDownEvent', async () => {
        it.todo('🤷🏼');
    });
}

// #endregion
